/**
 * THE BYTES BECOME A PICTURE ONLY WHEN THEY ARE A PNG.
 *
 * `GET /v1/branches/{branchCode}/logo` answers `application/octet-stream`, not
 * `image/png`, so the type alone says nothing; and the CSP (`scripts/csp.ts`)
 * allows images from `'self'` and `data:` only, so a `blob:` URL is refused in
 * a production build and is not added. The screen reads the bytes itself: an
 * empty download (a branch with no file — the document names no status for
 * one) and anything that is not a PNG both answer `null`, and the caller draws
 * its fallback. A non-PNG is still uploaded — the refusal is the server's
 * (`4003` is prose; `errorText` prints its `message`) — it just is not
 * previewed. There is no client sentence for a file that will not paint.
 *
 * The `data:` URL is built in memory and handed to the caller. It is never
 * written to localStorage, the address bar or a log.
 *
 * Pure on purpose: nothing here imports the query layer, so the route guard
 * (`routes/_authenticated.tsx`) can run it without pulling `useQuery` onto the
 * first-paint path.
 */

/** The eight bytes every PNG opens with. */
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

/**
 * The branch's logo as a paintable `data:` URL, or null when there is nothing
 * to paint: an empty blob, or bytes that are not a PNG.
 */
export async function logoSrc(blob: Blob): Promise<string | null> {
  if (blob.size === 0) return null;
  const bytes = new Uint8Array(await blob.arrayBuffer());
  if (PNG_SIGNATURE.some((byte, i) => bytes[i] !== byte)) return null;
  // `String.fromCharCode` spread over the whole array overflows the argument
  // list past ~64k, so the bytes go in chunks.
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return `data:image/png;base64,${btoa(binary)}`;
}
